import { useEffect, useState, useCallback } from 'react';
import { pusherManager } from './PusherHandler';

/**
 * Take Order Pusher Hook - Real-time WebSocket connection for order updates
 * Uses the SAME singleton Pusher instance as table-pusher (no extra connections)
 * 
 * This hook subscribes to take-order specific channels and events while
 * reusing the existing Pusher connection for optimal performance.
 * 
 * @example Basic Usage:
 * ```jsx
 * import useTakeOrderPusher from './path/to/take-order-pusher';
 * 
 * const OrderComponent = () => {
 *   const { 
 *     connectionStatus, 
 *     lastOrderUpdate, 
 *     error, 
 *     addOrderEventHandler,
 *     isConnected 
 *   } = useTakeOrderPusher();
 * 
 *   useEffect(() => {
 *     // Add custom order event handler
 *     addOrderEventHandler('order-completed', (data) => {
 *       console.log('Order completed:', data);
 *     });
 *   }, [addOrderEventHandler]);
 * 
 *   return (
 *     <div>
 *       <p>Order System Status: {connectionStatus}</p>
 *       {lastOrderUpdate && (
 *         <p>Latest Order Update: {JSON.stringify(lastOrderUpdate)}</p>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */

const useTakeOrderPusher = () => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [lastOrderUpdate, setLastOrderUpdate] = useState(null);
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [orderChannel, setOrderChannel] = useState(null);

    useEffect(() => {
        // Ensure pusher manager is initialized (it should be from table-pusher)
        pusherManager.initialize().catch(err => {
            console.error('Failed to ensure Pusher initialization:', err);
        });

        // Subscribe to pusher manager updates for connection status
        const unsubscribe = pusherManager.subscribe((type, data) => {
            if (type === 'status') {
                setConnectionStatus(data.connectionStatus);
                setError(data.error);
                setIsConnected(data.isConnected);
                
                // Subscribe to order-specific channel when connected
                if (data.isConnected && pusherManager.pusher) {
                    subscribeToOrderChannel();
                }
            }
        });

        return unsubscribe;
    }, []);

    // Subscribe to order-specific channel
    const subscribeToOrderChannel = useCallback(() => {
        if (!pusherManager.pusher) return;

        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;

            const user = JSON.parse(userStr);
            const restaurantId = user.restaurantId || '1';
            
            // Subscribe to order-specific channel using the same Pusher instance
            const orderChannelName = `private-restaurant-${restaurantId}-orders`;
            const channel = pusherManager.pusher.subscribe(orderChannelName);
            setOrderChannel(channel);

            // Bind default order event handlers
            addDefaultOrderHandlers(channel);

            console.log(`Subscribed to order channel: ${orderChannelName}`);
        } catch (err) {
            console.error('Failed to subscribe to order channel:', err);
        }
    }, []);

    // Add default order event handlers
    const addDefaultOrderHandlers = useCallback((channel) => {
        if (!channel) return;

        // Order status updates
        channel.bind('order-status-update', (data) => {
            console.log('Order Status Update:', data);
            setLastOrderUpdate({ 
                type: 'order-status-update', 
                data, 
                timestamp: Date.now() 
            });
        });

        // New order received
        channel.bind('new-order-received', (data) => {
            console.log('New Order Received:', data);
            setLastOrderUpdate({ 
                type: 'new-order-received', 
                data, 
                timestamp: Date.now() 
            });
        });

        // Order payment update
        channel.bind('order-payment-update', (data) => {
            console.log('Order Payment Update:', data);
            setLastOrderUpdate({ 
                type: 'order-payment-update', 
                data, 
                timestamp: Date.now() 
            });
        });

        // Kitchen status update
        channel.bind('kitchen-status-update', (data) => {
            console.log('Kitchen Status Update:', data);
            setLastOrderUpdate({ 
                type: 'kitchen-status-update', 
                data, 
                timestamp: Date.now() 
            });
        });
    }, []);

    // Method to add custom order event handlers
    const addOrderEventHandler = useCallback((eventName, handler) => {
        if (orderChannel) {
            orderChannel.bind(eventName, (data) => {
                handler(data);
                setLastOrderUpdate({ 
                    type: eventName, 
                    data, 
                    timestamp: Date.now() 
                });
            });
        } else {
            console.warn('Order channel not available. Make sure Pusher is connected.');
        }
    }, [orderChannel]);

    // Method to remove order event handlers
    const removeOrderEventHandler = useCallback((eventName, handler) => {
        if (orderChannel && handler) {
            orderChannel.unbind(eventName, handler);
        }
    }, [orderChannel]);

    // Method to send order-specific messages
    const sendOrderMessage = useCallback((eventName, data) => {
        if (orderChannel && connectionStatus === 'connected') {
            try {
                orderChannel.trigger(eventName, data);
                return true;
            } catch (err) {
                console.error('Failed to send order message:', err);
                return false;
            }
        }
        return false;
    }, [orderChannel, connectionStatus]);

    return {
        // Connection info (shared with table-pusher)
        pusher: pusherManager.pusher,
        connectionStatus,
        error,
        isConnected,
        
        // Order-specific functionality
        orderChannel,
        lastOrderUpdate,
        addOrderEventHandler,
        removeOrderEventHandler,
        sendOrderMessage,
        
        // Shared functionality
        reconnect: pusherManager.reconnect.bind(pusherManager),
    };
};

export default useTakeOrderPusher;